import { useCallback, useEffect, useState } from "react";

import { useNavigate, useSearchParams } from "react-router-dom";

import { toast, ToastContainer } from "react-toastify";

import ProgressBar from "../../../../shared/LinearProgressBar";

import { ASSESSMENT_ALLOWED, LMS_COURSE_ID, LMS_COURSE_NAME } from "../../../../utils/cookiesmanager";

import Button from "../ButtonComponent";

import QuestionCards from "../QuestionCards";

import HeaderModule from "../ModuleHeaderComponent";

import { BUTTON_VARIANTS } from "../constant";

import AssessmentExitModal from "../AssessmentExitModal";

import Shimmer from "../ShimmerComponent";

import { useGetPreAssessmentQuestion } from "./queries";

import { useSavePreAssessment } from "./mutation";

import styles from "./styles.module.scss";

const PreCourseAssessment = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [animate, setAnimate] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(10 * 60);
  const [allowLeave, setAllowLeave] = useState(false);
  const [showWarning, setShowWarning] = useState(false);

  const [searchParams] = useSearchParams();

  const languageid = localStorage.getItem("l_id") || 1;

  const courseId = searchParams.get("courseId") || localStorage.getItem(LMS_COURSE_ID);
  const courseName = searchParams.get("courseName") || localStorage.getItem(LMS_COURSE_NAME);

  const navigate = useNavigate();

  const { data: preAssessmentQuestion, isLoading } =
    useGetPreAssessmentQuestion({ courseId, languageid });

  const { mutate, isPending } = useSavePreAssessment();

  const { data: preAssessmentQuestionData } = preAssessmentQuestion || {};

  const { response } = preAssessmentQuestionData || {};

  const handleSubmit = () => {
    mutate(selectedAnswer, {
      onSuccess(data) {
        const { status, error } = data;

        if (!status) {
          throw new Error(error);
        }

        if (isLastQuestion) {
          navigate(
            `/user/module-list?courseId=${courseId}&courseName=${courseName}`,
          );
          return;
        }
        setAnimate(true);
        setSelectedAnswer(null);

        setTimeout(() => {
          setAnimate(false);
          setActiveIndex((prev) => prev + 1);
        }, 500); // animation duration
      },
      onError(error) {
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        toast(errorMessage, { type: "error" });
      },
      onSettled() {},
    });
  };

  const handleAutoSubmit = () => {
    handleSubmit();
  };
  

  useEffect(() => {
    // Add a dummy history entry
    window.history.pushState(
      { guard: true },
      "",
      `${window.location.pathname}?${searchParams.toString()}`,
    );

    const handlePopState = () => {
      if (!allowLeave) {
        setShowWarning(true);

        // Re-push state to stop navigation
        window.history.pushState({ guard: true }, "", window.location.pathname);
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [allowLeave]);

  const handleQuestionAnswer = useCallback((question, option) => {
    setSelectedAnswer({
      courseId: courseId,
      languageId: languageid,
      assessmentId: question?.assessmentId,
      assessmentQuestionId: question?.assQuestionId,
      assessmentAnswerId: option?.assAnswerId,
    });
  }, []);

  useEffect(() => {
    // if (!showTimer) return;
    if (secondsLeft <= 0) {
      handleAutoSubmit?.();
      return;
    }

    const timer = setInterval(() => {
      setSecondsLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [secondsLeft]);

  const minutes = String(Math.floor(secondsLeft / 60)).padStart(2, "0");
  const seconds = String(secondsLeft % 60).padStart(2, "0");

  const isDanger = secondsLeft <= 30;

  const isLastQuestion = response && activeIndex === response?.length - 1;

  const handleBack = () => {
    setShowWarning(true);
  };

  const handleContinueAssessment = () => {
    setShowWarning(false);
    setAllowLeave(false);
  };

  const handleExit = () => {
    sessionStorage.removeItem(ASSESSMENT_ALLOWED);
    navigate(
      `/user/module-list?courseId=${courseId}&courseName=${courseName}`,
    );
  };

  useEffect(() => {
    // Add a dummy history entry
    window.history.pushState(
      { guard: true },
      "",
      `${window.location.pathname}?${searchParams.toString()}`,
    );

    const handlePopState = () => {
      if (!allowLeave) {
        setShowWarning(true);

        // Re-push state to stop navigation
        window.history.pushState({ guard: true }, "", window.location.pathname);
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [allowLeave]);

  useEffect(() => {
        const allowed = sessionStorage.getItem(ASSESSMENT_ALLOWED);
      
        if (!allowed) {
          navigate(
            `/user/module-list?courseId=${courseId}&courseName=${courseName}`,
            {replace:true}
          );
        }
  }, []);
  
  return (
    <div className={styles.page}>
      <HeaderModule
        title="Pre-Course Assessment"
        className={styles["header-title"]}
        minutes={minutes}
        seconds={seconds}
        isDanger={isDanger}
        onBackClick={handleBack}
      />
      <div className={styles["container-wrapper"]}>
        <div className={styles["content-part"]}>
          <p className={styles.title}>{courseName}</p>
          <ProgressBar current={activeIndex} total={response?.length} />
          {isLoading ? (
            <Shimmer width="90%" height="324px" borderRadius="14px" />
          ) : (
            <QuestionCards
              selectedAnswer={selectedAnswer}
              questionsList={response}
              handleQuestionAnswer={handleQuestionAnswer}
              activeIndex={activeIndex}
              animate={animate}
            />
          )}
        </div>
      </div>
      <div className={styles["bottom-wrapper"]}>
        <Button
          loading={isPending}
          label={
            isLastQuestion
              ? isPending
                ? "Submitting..."
                : "Continue Learning"
              : isPending
                ? "Submitting..."
                : "Next"
          }
          buttonVariant={BUTTON_VARIANTS.PRIMARY}
          className={styles["button-class"]}
          onClick={handleSubmit}
          disabled={!selectedAnswer || isPending}
        />
      </div>

      {showWarning && (
        <AssessmentExitModal
          handleContinueAssessment={handleContinueAssessment}
          handleExit={handleExit}
          show={showWarning}
        />
      )}

      <ToastContainer />
    </div>
  );
};

export default PreCourseAssessment;
